import { calculateStateVAExemption } from '@/utils/calculator/stateVAExemptions';

/**
 * Calculate Texas VA disability property tax exemption
 */
export const calculateTexasVADisabilityTaxExemption = (
  homePrice: number,
  vaDisabilityStatus: "none" | "partial" | "full" | null,
  vaDisabilityRatingPercentage: number | null,
  veteranAge?: number | null
): number => {
  if (!vaDisabilityStatus || vaDisabilityStatus === "none") {
    return 0;
  }

  // 100% disability rating = full exemption
  if (vaDisabilityStatus === "full") {
    return homePrice; // Full exemption
  }

  // Partial disability exemptions - use exact percentage if provided
  if (vaDisabilityStatus === "partial") {
    let baseExemption = 5000; // Default minimum
    
    if (vaDisabilityRatingPercentage) {
      // Texas tiered exemption structure
      if (vaDisabilityRatingPercentage >= 10 && vaDisabilityRatingPercentage <= 29) {
        baseExemption = 5000;
      } else if (vaDisabilityRatingPercentage >= 30 && vaDisabilityRatingPercentage <= 49) {
        baseExemption = 7500;
      } else if (vaDisabilityRatingPercentage >= 50 && vaDisabilityRatingPercentage <= 69) {
        baseExemption = 10000;
      } else if (vaDisabilityRatingPercentage >= 70 && vaDisabilityRatingPercentage <= 99) {
        baseExemption = 12000;
      }
    } else {
      // No exact percentage provided, use average
      baseExemption = 10000;
    }
    
    let totalExemption = baseExemption;
    
    // Additional exemption for veterans 65+ with 10%+ disability
    if (veteranAge && veteranAge >= 65) {
      totalExemption += 12000;
    }
    
    return Math.min(totalExemption, homePrice);
  }

  return 0;
};

/**
 * Calculate monthly property tax based on property tax rate (excluding PID)
 */
export const calculateMonthlyPropertyTax = (
  homePrice: number,
  propertyTaxRate: number,
  vaDisabilityStatus: "none" | "partial" | "full" | null,
  vaDisabilityRatingPercentage: number | null,
  loanType: "conventional" | "fha" | "va" | "usda",
  state?: string,
  veteranAge?: number | null
): number => {
  // VA full disability exemption (universal)
  if (loanType === "va" && vaDisabilityStatus === "full") {
    return 0;
  }

  // State-specific VA disability exemptions (including Texas and others)
  if (loanType === "va" && vaDisabilityStatus && state) {
    const exemptionResult = calculateStateVAExemption(
      state,
      vaDisabilityStatus,
      vaDisabilityRatingPercentage,
      veteranAge,
      homePrice
    );
    const taxableValue = exemptionResult.taxableValue;
    
    if (taxableValue <= 0) {
      return 0; // Full exemption
    }
    
    // Use taxable value instead of home price
    const annualRate = propertyTaxRate && propertyTaxRate > 0 
      ? (propertyTaxRate < 1 ? propertyTaxRate : propertyTaxRate / 100)
      : 0.0200; // Default rate
    
    return (taxableValue * annualRate) / 12;
  }

  // If no rate provided, use default 2.00%
  if (!propertyTaxRate || propertyTaxRate <= 0) {
    return (homePrice * 0.0200) / 12;
  }
  
  // Convert percentage to decimal if needed
  let annualRate: number;
  
  if (propertyTaxRate < 1) {
    // Already a decimal rate (e.g., 0.0254 for 2.54%)
    annualRate = propertyTaxRate;
  } else {
    // Percentage format (e.g., 2.54 for 2.54%)
    annualRate = propertyTaxRate / 100;
  }
  
  // Calculate monthly property tax
  return (homePrice * annualRate) / 12;
};

/**
 * Calculate monthly PID assessment
 * PID (Public Improvement District) assessments are NOT property taxes
 * and are NOT exempt for VA disabled veterans
 */
export const calculateMonthlyPIDAssessment = (
  homePrice: number,
  pidRate: number
): number => {
  if (!pidRate || pidRate <= 0) {
    return 0;
  }
  
  // PID rates are always entered as percentages (e.g., 0.54 for 0.54%)
  // Convert to decimal by dividing by 100
  const annualRate = pidRate / 100;
  
  // Calculate monthly PID assessment
  return (homePrice * annualRate) / 12;
};

/**
 * Calculate monthly home insurance based on home price
 */
export const calculateMonthlyHomeInsurance = (
  homePrice: number,
  insuranceRate = 0.0035 // Default 0.35% annually per NAIC standards
): number => {
  return (homePrice * insuranceRate) / 12;
};

/**
 * Get property tax rate as percentage for display
 */
export const getPropertyTaxRateDisplay = (propertyTaxRate: number): string => {
  if (!propertyTaxRate || propertyTaxRate <= 0) {
    return "2.00%"; // Default rate
  }
  
  if (propertyTaxRate < 1) {
    return `${(propertyTaxRate * 100).toFixed(2)}%`;
  } else {
    return `${propertyTaxRate.toFixed(2)}%`;
  }
};
